import {
  Button,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { TeamsContext } from "../../controllers/teams"
import TeamGroup from "../../models/teamGroup"
import CopyToClipboardButton from "../global/copyToClipboardButton"
import { LoadingButton } from "@mui/lab"
import { ActivationCodesContext } from "../../controllers/activationCodes"
import { CheckRounded, DeleteRounded, EditRounded } from "@mui/icons-material"
import {
  ActivationCodePolicy,
  ActivationCodeType,
} from "../../services/config/enum"
import ActivationCode from "../../models/activationCode"
import ActivationCodeDomainsRow from "./activationCodeDomainsRow"

const GroupCard = ({
  group,
  index,
  groupsListMemo,
  setCopyFeedbackOpen,
}: {
  group: TeamGroup
  index: number
  groupsListMemo: TeamGroup[]
  setCopyFeedbackOpen: Dispatch<SetStateAction<boolean>>
}) => {
  const { currentTeam, setCurrentTeam, editMode } = useContext(TeamsContext)
  const { createActivationCode, upsertActivationCode } = useContext(
    ActivationCodesContext
  )

  // states
  const [loading, setLoading] = useState<boolean>(false)
  const [editing, setEditing] = useState<boolean>(false)

  useEffect(() => {
    if (!editMode) {
      setEditing(false)
    }
  }, [editMode])

  // current group activation code
  const activationCode: ActivationCode | undefined = useMemo(
    () =>
      currentTeam.accessCodes.find((item) => item.groupId === group.groupId),
    [currentTeam]
  )

  // activation code domains management states
  const [currentDomainsWhiteList, setCurrentDomainsWhiteList] = useState<
    string[] | undefined
  >(activationCode?.domainsWhiteList)
  const [domainToAdd, setDomainToAdd] = useState<string>("")

  // reset data when user cancels edit
  useEffect(() => {
    if (!editing) {
      setCurrentDomainsWhiteList(activationCode?.domainsWhiteList)
      setDomainToAdd("")
    }
  }, [editing])

  // if group has been just created
  const isGroupNew = useMemo(() => group.groupId.includes("temp"), [group])

  return (
    <Card
      id={group.groupId}
      variant="outlined"
      sx={{
        boxShadow: "none",
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius:
          index === groupsListMemo.length - 1 ? undefined : 0,
        borderBottomRightRadius:
          index === groupsListMemo.length - 1 ? undefined : 0,
        borderTop: index === 0 ? undefined : "none",
        transition: "200ms",
      }}
    >
      <CardContent
        style={{
          minHeight: 68,
          paddingTop: 12,
          paddingBottom: editing && !activationCode ? 0 : 12,
        }}
      >
        <Stack
          spacing={1}
          style={{ position: "relative" }}
          justifyContent="center"
        >
          {editing ? (
            <TextField
              fullWidth
              size="small"
              value={group.name}
              onChange={(e) => {
                currentTeam.groups.find(
                  (item) => item.groupId === group.groupId
                ).name = e.target.value
                setCurrentTeam({ ...currentTeam })
              }}
              disabled={!editMode || loading}
              style={{
                width: "calc(100% - 400px)",
                marginTop: -5,
              }}
            />
          ) : (
            <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
              {group.name}
            </Typography>
          )}
          {!isGroupNew && (
            <Stack
              direction="row"
              alignItems="center"
              style={{ marginTop: 0, marginLeft: -4 }}
            >
              <CopyToClipboardButton textToCopy={group.groupId} />
              <Typography
                variant="caption"
                style={{
                  fontSize: 10,
                  color: "gray",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {group.groupId}
              </Typography>
            </Stack>
          )}
          {currentDomainsWhiteList &&
          currentDomainsWhiteList.length &&
          !editing ? (
            currentDomainsWhiteList.length > 3 ? (
              <Stack gap={0.5} direction="row" style={{ marginTop: 0 }}>
                <Typography style={{ fontSize: "0.8rem" }}>
                  {`Whitelisted for ${currentDomainsWhiteList
                    .slice(0, 3)
                    .join(", ")}`}
                </Typography>
                <Tooltip
                  title={currentDomainsWhiteList.slice(3).join(", ")}
                  arrow
                  sx={{ cursor: "pointer" }}
                >
                  <Typography style={{ fontSize: "0.8rem" }}>
                    {`and ${currentDomainsWhiteList.slice(3).length} more`}
                  </Typography>
                </Tooltip>
              </Stack>
            ) : (
              <Typography style={{ fontSize: "0.8rem", marginTop: 0 }}>
                {`Whitelisted for ${currentDomainsWhiteList.join(", ")}`}
              </Typography>
            )
          ) : null}
          {!isGroupNew && (
            <div
              dangerouslySetInnerHTML={{
                __html: `
                <p style="line-height: 1.3"><span style="font-weight: 500; font-size: 0.9rem">${
                  group.userCount
                }</span> <span style="font-weight: 300; font-size: 0.9rem">members</span> | <span style="font-weight: 500; font-size: 0.9rem">${
                  currentTeam.userCount
                    ? Math.round(
                        (group.userCount / currentTeam.userCount) * 100
                      )
                    : 0
                }</span>%</p>`,
              }}
              style={{
                fontWeight: 400,
                fontSize: "0.7rem",
                lineHeight: 1.1,
                marginTop: 8,
                position: "absolute",
                right: 105,
                top:
                  currentDomainsWhiteList && currentDomainsWhiteList.length
                    ? 4
                    : -5,
              }}
            />
          )}
          {!isGroupNew && (
            <Stack
              direction="row"
              style={{
                height: 21.5,
                position: "absolute",
                right: 105,
                top:
                  currentDomainsWhiteList && currentDomainsWhiteList.length
                    ? 24
                    : 15,
              }}
              alignItems="center"
            >
              <Typography
                style={{
                  fontSize: "0.9rem",
                  fontWeight: 300,
                }}
              >
                {editing && !activationCode
                  ? "Activation code:"
                  : activationCode
                  ? "Activation code:"
                  : "Activation code: none"}
              </Typography>
              {activationCode ? (
                <Stack direction="row" style={{ height: 21.59 }} spacing={0.5}>
                  <Typography
                    style={{
                      fontSize: "0.9rem",
                      fontWeight: 400,
                      marginLeft: 5,
                    }}
                  >
                    {activationCode.code}
                  </Typography>
                  <CopyToClipboardButton textToCopy={activationCode.code} />
                </Stack>
              ) : null}
              {editing && !activationCode ? (
                <LoadingButton
                  style={{ maxHeight: "100%" }}
                  loading={loading}
                  onClick={async () => {
                    setLoading(true)

                    const result = await createActivationCode(
                      ActivationCodeType.TeamActivationCode,
                      ActivationCodePolicy.IterationUnlimited,
                      null,
                      { teamId: currentTeam.id, groupId: group.groupId }
                    )
                    if (typeof result !== "boolean") {
                      currentTeam.accessCodes.push(result)
                      setCurrentTeam({ ...currentTeam })
                    }

                    setLoading(false)
                  }}
                >
                  Create
                </LoadingButton>
              ) : null}
            </Stack>
          )}
          <IconButton
            size="small"
            style={{
              width: 40,
              height: 40,
              position: "absolute",
              right: 40,
              top:
                currentDomainsWhiteList && currentDomainsWhiteList.length
                  ? 4
                  : -5,
              transition: "150ms",
            }}
            onClick={async () => {
              if (editing && activationCode) {
                setLoading(true)

                const result = await upsertActivationCode(
                  activationCode.code,
                  activationCode.codeType,
                  activationCode.policy,
                  activationCode.maxIteration,
                  {
                    teamId: activationCode.teamId,
                    groupId: activationCode.groupId,
                  },
                  currentDomainsWhiteList,
                  activationCode.isPrimary
                )

                if (result) {
                  setCurrentTeam((current) => {
                    const activationCodeIndex = current.accessCodes.findIndex(
                      (item) => item.code === activationCode.code
                    )

                    return {
                      ...current,
                      accessCodes: current.accessCodes
                        ? [
                            ...current.accessCodes.slice(
                              0,
                              activationCodeIndex
                            ),
                            result,
                            ...current.accessCodes.slice(
                              activationCodeIndex + 1
                            ),
                          ]
                        : current.accessCodes,
                    }
                  })
                }

                setLoading(false)
              }

              setEditing((current) => !current)
            }}
            disabled={!editMode || loading}
          >
            {editing ? (
              <CheckRounded
                style={{
                  width: 24,
                }}
              />
            ) : (
              <EditRounded
                style={{
                  width: 20,
                }}
              />
            )}
          </IconButton>
          <IconButton
            size="small"
            color="error"
            style={{
              width: 40,
              height: 40,
              position: "absolute",
              right: -11,
              top:
                currentDomainsWhiteList && currentDomainsWhiteList.length
                  ? 4
                  : -5,
              transition: "150ms",
            }}
            onClick={() => {
              setCurrentTeam((current) => ({
                ...current,
                groups: current.groups.filter(
                  (item) => item.groupId !== group.groupId
                ),
              }))
            }}
            disabled={!editMode || loading}
          >
            <DeleteRounded
              style={{
                width: 24,
              }}
            />
          </IconButton>
          {editing && !isGroupNew && activationCode ? (
            <Stack>
              <Stack gap={0.5} direction="row" alignItems="center">
                <Typography style={{ fontSize: "0.9rem", fontWeight: 600 }}>
                  Domains Whitelist
                </Typography>
                {currentDomainsWhiteList && currentDomainsWhiteList.length ? (
                  <Typography style={{ fontSize: "0.8rem" }}>
                    (hover to show delete button)
                  </Typography>
                ) : null}
              </Stack>
              {currentDomainsWhiteList
                ? currentDomainsWhiteList.map((domain, index) => (
                    <ActivationCodeDomainsRow
                      key={index}
                      domain={domain}
                      index={index}
                      setCopyFeedbackOpen={setCopyFeedbackOpen}
                      currentDomainsWhiteList={currentDomainsWhiteList}
                      setCurrentDomainsWhiteList={setCurrentDomainsWhiteList}
                      disabled={loading}
                    />
                  ))
                : null}
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  setCurrentDomainsWhiteList((current) =>
                    current
                      ? [...current, domainToAdd.trim()]
                      : [domainToAdd.trim()]
                  )
                  setDomainToAdd("")
                }}
              >
                <Stack gap={1} direction="row" style={{ marginTop: 8 }}>
                  <TextField
                    size="small"
                    label="Domain"
                    value={domainToAdd}
                    onChange={(e) => setDomainToAdd(e.target.value)}
                    disabled={loading}
                  />
                  <Button
                    variant="contained"
                    disabled={
                      !/^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/.test(domainToAdd) ||
                      loading
                    }
                    type="submit"
                  >
                    Add
                  </Button>
                </Stack>
              </form>
            </Stack>
          ) : null}
        </Stack>
        {loading && (
          <LinearProgress
            style={{ width: "100%", position: "absolute", left: 0, bottom: 0 }}
          />
        )}
      </CardContent>
    </Card>
  )
}

export default GroupCard
